import type { LactateClient } from '../types';
import type { ClientDirectoryClient } from '../../clients/services/clientDirectoryService';

/**
 * Raw inputs for a client who is not yet in the database. The Mindbody client
 * id becomes the document key for the lactate test (keeping the same keying
 * scheme as imported clients), so it must be a safe Firestore path segment.
 */
export interface ManualClientValues {
  mindbody_id: string;
  first_name: string;
  surname: string;
  email: string;
}

export const emptyManualClient = (): ManualClientValues => ({
  mindbody_id: '',
  first_name: '',
  surname: '',
  email: '',
});

// A Firestore document id cannot contain '/', cannot be '.' or '..', and we
// additionally forbid whitespace so the Mindbody id stays a clean path key.
const SAFE_ID_RE = /^[A-Za-z0-9._-]+$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateManualClient(values: ManualClientValues): Record<string, string> {
  const errors: Record<string, string> = {};

  const id = values.mindbody_id.trim();
  if (id === '') {
    errors.mindbody_id = 'Required';
  } else if (id === '.' || id === '..' || !SAFE_ID_RE.test(id)) {
    errors.mindbody_id = 'Use letters, digits, . _ or - only (no spaces or /)';
  }

  if (values.first_name.trim() === '') errors.first_name = 'Required';
  if (values.surname.trim() === '') errors.surname = 'Required';

  const email = values.email.trim();
  if (email === '') errors.email = 'Required';
  else if (!EMAIL_RE.test(email)) errors.email = 'Enter a valid email';

  return errors;
}

/** Build the in-memory client used to drive the test form. No DB write happens
 *  here — the test (and its implied client keying) is only persisted when the
 *  coach generates the report. */
export function toLactateClient(values: ManualClientValues): LactateClient {
  return {
    client_id: values.mindbody_id.trim(),
    client_name: `${values.first_name.trim()} ${values.surname.trim()}`.trim(),
    email: values.email.trim(),
    source: 'manual',
  };
}

/** Map a directory client (found via the omnibox) into the same shape. */
export function directoryToLactateClient(c: ClientDirectoryClient): LactateClient {
  return {
    client_id: c.client_id,
    client_name: c.client_name,
    primary_location: c.primary_location || undefined,
    email: c.email || undefined,
    source: 'directory',
  };
}
